import { useAxios } from '@healthblocks-io/core'
import { buildWebStorage, setupCache } from 'axios-cache-interceptor'
import { useCallback, useMemo } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'

import type { AxiosError } from 'axios'

const fromLocalStorage = buildWebStorage(localStorage, 'azure-maps-cache:')

function useAzureMaps() {
  const authAxios = useAxios()

  const axiosInstance = useMemo(
    () =>
      setupCache(authAxios, {
        ttl: 15 * 60 * 1000, // Default 15 minutes
        interpretHeader: false,
        methods: ['get'],
        cachePredicate: {
          statusCheck: (status) => status >= 200 && status < 400,
        },
        storage: fromLocalStorage,
      }),
    [authAxios],
  )

  // Methods
  // ------------------------------------------------------------------------- /
  const searchAddress = useCallback(
    async (query: string, countrySet?: string) => {
      const country = (countrySet ?? '').trim()

      try {
        return await axiosInstance.get('/azure-maps/search', {
          params: {
            query,
            ...(country ? { countrySet: country } : null),
          },
        })
      } catch (error) {
        const axiosError = error as AxiosError

        // Some country codes / autofilled values can cause 400s. Retry without countrySet
        // to keep address lookup usable.
        if (axiosError.response?.status === 400 && country) {
          return await axiosInstance.get('/azure-maps/search', {
            params: {
              query,
            },
          })
        }

        throw error
      }
    },
    [axiosInstance],
  )

  return {
    searchAddress: useAxiosAsync(searchAddress),
  }
}

export { useAzureMaps }
